import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import _ from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { NotificationType } from 'src/app/shared/common/components/gsp-notification/gsp-notification.component';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import {
    CreatePanelView,
    DetailPanelView,
    PanelViewMode
} from 'src/app/shared/common/enums/list-panel/panel-view-mode.enum';
import { TCFile } from 'src/app/shared/common/files/TCFile';
import { PanelMenuService } from 'src/app/shared/common/services/panel-menu/panel-menu.service';
import { GeneralUtils } from 'src/app/shared/common/utility/general-utils';
import { UtilitiesService } from 'src/app/shared/common/utility/utilities.service';
import {
    mapToSurveyJobAttachment,
    SurveyJob,
    SurveyJobAttachment,
    SurveyJobCreateData
} from 'src/app/shared/survey-data-services/survey-job';
import { SurveyJobPreloadService } from 'src/app/shared/survey-data-services/survey-job-preload.service';
import { SurveyJobService } from 'src/app/shared/survey-data-services/survey-job.services';
import { User } from 'src/app/shared/user/user';

@Component({
    selector: 'create-survey-job',
    templateUrl: './create-survey-job.component.html'
})
export class CreateSurveyJobComponent implements OnInit, OnDestroy {
    @Output() navigateTo = new EventEmitter<PanelViewMode>();

    @ViewChild('surveyJobNameForm')
    public surveyJobNameForm: NgForm;

    private invalidCharsRegEx = /[@^+\[\]"*\/:<>?\\|$]/g;
    newJob = new SurveyJob({ assignees: [], tags: [], linkedFiles: [], name: '', description: '' });

    destroyed = new Subject<void>();
    ButtonType = ButtonType;
    NotificationType = NotificationType;

    headerTitle = 'Survey.SurveyCreatePanel.NewJob';
    isJobCreationInProgress = false;
    loading = true;
    isAttachFilesView = false;
    canCreateSurveyJob = false;
    settingsPageUrl: string;

    constructor(
        private panelMenuService: PanelMenuService,
        private surveyJobService: SurveyJobService,
        private jobPreloadService: SurveyJobPreloadService,
        private messaging: MessagingService,
        private translate: TranslationService,
        private projectStreamService: ProjectStreamService,
        private utilities: UtilitiesService
    ) {}

    ngOnInit(): void {
        this.panelMenuService.createPanelViewStream.pipe(takeUntil(this.destroyed)).subscribe(view => {
            this.isAttachFilesView = view === CreatePanelView.AttachFiles;
            this.headerTitle = this.isAttachFilesView
                ? 'Survey.SurveyEditPanel.LinkFiles'
                : 'Survey.SurveyCreatePanel.NewJob';
        });

        this.projectStreamService.currentProjectStream.pipe(takeUntil(this.destroyed)).subscribe(project => {
            if (project) {
                this.settingsPageUrl = this.utilities.getTCAppUrl() + 'project/' + project.id + '/settings/details';
                this.canCreateSurveyJob = project.crs ? true : false;
                if (!project.crs) {
                    this.projectStreamService.checkAndUpdateCRSProject(project.id).finally(() => {
                        this.loading = false;
                    });
                    return;
                }
                this.loading = false;
            }
        });
    }

    ngOnDestroy(): void {
        //Reset the panel view to default when component is destroyed
        this.panelMenuService.createPanelViewStream.next(CreatePanelView.Create);
        this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Detail);
        this.destroyed.next();
        this.destroyed.complete();
    }

    onNameInputChange(input: string): void {
        // Remove invalid characters from job name input
        const sanitizedInput = input.replace(this.invalidCharsRegEx, '');
        this.newJob.name = sanitizedInput.trim();
    }

    blockInvalidChars(event: KeyboardEvent): void {
        if (this.invalidCharsRegEx.test(event.key)) {
            event.preventDefault();
        }
    }

    onDescriptionInputChange(input: string): void {
        this.newJob.description = input;
    }

    onTagsInputChange(input: string[]): void {
        this.newJob.tags = input;
    }

    onAssignedUserChange(input: User[]): void {
        this.newJob.assignees = input;
    }

    onLinkedFilesInputChange(input: TCFile[] | SurveyJobAttachment[], isRemoveChange: boolean = false): void {
        if (isRemoveChange) {
            this.newJob.linkedFiles = _.cloneDeep(input) as SurveyJobAttachment[];
        } else {
            this.newJob.linkedFiles = mapToSurveyJobAttachment(input as TCFile[]);
        }
    }

    cancelCreateAction(): void {
        this.backActionHandler();
    }

    getNewJobData(): SurveyJobCreateData {
        return {
            name: this.newJob.name,
            description: this.newJob.description,
            assigneeUserId: this.newJob.assignees?.length ? this.newJob.assignees[0].id : '',
            tags: this.newJob.tags,
            linkedFiles: this.newJob.linkedFiles ? this.newJob.linkedFiles.map(file => file.id) : []
        };
    }

    createJob(): void {
        if (this.isJobNameEntered()) {
            this.isJobCreationInProgress = true;
            const newJobData = this.getNewJobData();
            this.surveyJobService.createSurveyJob(newJobData).then(
                response => {
                    this.isJobCreationInProgress = false;
                    this.messaging.showSuccess(this.translate.instant('Survey.SurveyCreatePanel.CreateJobSuccess'));
                    this.jobPreloadService.reloadJobListStream.next(true);
                    this.navigateTo.emit(PanelViewMode.List);
                },
                error => {
                    this.messaging.showError(this.translate.instant('Survey.SurveyCreatePanel.CreateJobError'));
                    this.isJobCreationInProgress = false;
                }
            );
        }
    }

    isJobUpdated(): boolean {
        return (
            this.newJob.name.trim() !== '' ||
            this.newJob.description.trim() !== '' ||
            this.newJob.assignees?.length > 0 ||
            this.newJob.tags?.length > 0 ||
            this.newJob.linkedFiles?.length > 0
        );
    }

    isJobNameEntered(): boolean {
        const nameControl = this.surveyJobNameForm?.controls['jobNameInput'];
        const isValid = nameControl ? nameControl.valid : false;
        return this.newJob.name.trim() !== '' && isValid;
    }

    private getSurveyJobNameValidationError(errors: object): string {
        let errorMessage = null;
        if (errors) {
            if (_.filter([errors], 'required').length) {
                return 'RequiredError';
            } else if (_.filter([errors], 'invalidName').length) {
                return 'Survey.SurveyCreatePanel.InvalidJobName';
            } else if (_.filter([errors], 'exceedsMaxLength').length) {
                return 'Survey.SurveyCreatePanel.MaxLengthError';
            } else if (_.filter([errors], 'specialChars').length) {
                return 'Survey.SurveyCreatePanel.SpecialCharsError';
            } else if (_.filter([errors], 'notUnique').length) {
                return 'Survey.SurveyCreatePanel.JobNameAlreadyExist';
            }
        }
        return errorMessage;
    }

    public getErrorText(errors: object): string {
        if (GeneralUtils.isNullUndefinedOrNaN(errors)) {
            return null;
        } else {
            const errorMessage = this.getSurveyJobNameValidationError(errors);
            return errorMessage ? this.translate.instant(errorMessage) : null;
        }
    }

    backActionHandler(): void {
        if (!this.isAttachFilesView && this.isJobUpdated()) {
            this.panelMenuService.confirmationPopupDataStream.next({
                title: this.translate.instant('TC.MetadataEditor.DiscardChangesWithQuestion'),
                message: this.translate.instant('Survey.DiscardChangesQuestion'),
                confirmButtonText: this.translate.instant('discard'),
                isPermanentAction: false,
                id: 'discard-create-job-changes',
                confirmAction: () => Promise.resolve(this.goBackFromCurrentView())
            });
        } else {
            this.goBackFromCurrentView();
        }
    }

    goBackFromCurrentView(): void {
        if (this.isAttachFilesView) {
            this.panelMenuService.createPanelViewStream.next(CreatePanelView.Create);
        } else {
            !this.isJobCreationInProgress && this.navigateTo.emit(PanelViewMode.List);
        }
    }
}
